import { Box, type SxProps, type Theme, Typography, useTheme } from "@mui/material";
import { CheckIcon } from "components/icons";

interface OnboardingSelectionChipProps {
  label: string;
  isSelected: boolean;
  onClick: () => void;
  badge?: string;
  showCheck?: boolean;
  disabled?: boolean;
}

const getChipSx = (
  theme: Theme,
  isSelected: boolean,
  isMultiSelect: boolean,
  disabled: boolean,
): SxProps<Theme> => ({
  padding: "6px 8px",
  borderRadius: "4px",
  border: "1px solid",
  borderColor: isSelected
    ? theme.palette.secondary["purple-tint"]
    : theme.palette.primitive.neutrals.neutral[800],
  backgroundColor:
    isSelected && !isMultiSelect
      ? theme.palette.secondary["purple-tint"]
      : isSelected
        ? "rgba(135, 145, 248, 0.05)"
        : "transparent",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: disabled ? "not-allowed" : "pointer",
  transition: "all 0.2s ease",
  gap: "8px",
  userSelect: "none",
  opacity: disabled ? 0.4 : 1,
  pointerEvents: disabled ? "none" : "auto",
  "&:hover": {
    borderColor: isSelected
      ? theme.palette.secondary["purple-tint"]
      : theme.palette.primitive.neutrals.neutral[600],
  },
});

export const OnboardingSelectionChip = ({
  label,
  isSelected,
  onClick,
  badge,
  showCheck = false,
  disabled = false,
}: OnboardingSelectionChipProps): JSX.Element => {
  const theme = useTheme();

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onClick}
      onKeyDown={handleKeyDown}
      sx={getChipSx(theme, isSelected, showCheck, disabled)}
      aria-pressed={isSelected}
      aria-disabled={disabled}
      aria-label={label}
    >
      <Typography
        variant="body-small"
        sx={{
          fontWeight: isSelected && !showCheck ? 600 : 400,
        }}
        color={
          isSelected
            ? showCheck
              ? theme.palette.secondary["purple-tint"]
              : theme.palette.lightBg.main
            : "inherit"
        }
      >
        {label}
      </Typography>
      {badge && !isSelected && (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            padding: "4px 8px",
            borderRadius: "2px",
            backgroundColor:
              badge === "Coming soon"
                ? theme.palette.primitive.system.orange[900]
                : theme.palette.primitive.system.green[900],
          }}
        >
          <Typography
            variant="caption-xs"
            sx={{
              color:
                badge === "Coming soon"
                  ? theme.palette.primitive.system.orange[500]
                  : theme.palette.primitive.system.green[500],
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            &bull; {badge}
          </Typography>
        </Box>
      )}
      {showCheck && isSelected && (
        <CheckIcon
          sx={{ fontSize: 16, color: theme.palette.secondary["purple-tint"] }}
        />
      )}
    </Box>
  );
};
